/**
 * @fileoverview Admin routes component with authentication and role-based guards.
 */

import React, {useCallback, useEffect} from "react";
import {Switch} from "react-router-dom";
import {SentryRoute as Route} from "../utils/sentryRoute";

import {PageAdmin404} from "../pages/page-admin-404";
import {PageAdminDashboard} from "../pages/page-admin-dashboard";
import {PageAdminLogin} from "../pages/page-admin-login";
import {
  PageAdminChangePassword,
  PageAdminProfileEdit,
} from "../pages/page-admin-user-profile";
import {PageAdminFacilitySettings} from "../pages/page-admin-facility-settings";
import {PageAdminAdmissionExperience} from "../pages/page-admin-admission-experience";
import {PageAdminSearch} from "../pages/page-admin-search/PageAdminSearch";
import {PageAdminActivityLog} from "../pages/page-admin-activity-log";
import {PageAdminSSOLoginForm} from "../pages/page-admin-sso-login-form";
/* Users */
import {PageAdminUsers} from "../pages/page-admin-users";
import {
  PageAdminUserCreate,
  PageAdminUserEdit,
} from "../pages/page-admin-user-edit";

/* Residents */
import {PageAdminResidents} from "../pages/page-admin-residents";
import {ResidentProfile} from "../pages/page-admin-residents/atoms/ResidentProfile";
import {PageAdminResidentsCreate} from "../pages/page-admin-residents-forms/page-admin-residents-create/PageAdminResidentsCreate";
import {PageAdminResidentsEdit} from "../pages/page-admin-residents-forms/page-admin-residents-edit/PageAdminResidentsEdit";

/* Organizations */
import {PageAdminOrganizations} from "../pages/page-admin-organizations";
import {PageAdminOrganizationsCreate} from "../pages/page-admin-organizations-create";
import {PageAdminOrganizationsEdit} from "../pages/page-admin-organizations-edit";
import {PageAdminOrganizationImportExportUsers} from "../pages/page-admin-organizations-users";
import {PageAdminOrganizationsBranding} from "../pages/page-admin-organizations-branding";
import {
  PageAdminSsoConfiguration,
  PageAdminSsoConfigurationCreate,
  PageAdminSsoConfigurationEdit,
} from "../pages/page-admin-sso-configuration";
import {PageAdminFacilityCreate} from "../pages/page-admin-facility-create";

import {AdminIsLoggedIn} from "../models/admin-session/AdminIsLoggedIn";
import {AdminIsLoggedOut} from "../models/admin-session/AdminIsLoggedOut";
import {ReportingRouter} from "./ReportingRouter";

import {HubAndSpokeRouter} from "../pages/page-admin-hub-and-spoke";

import {PageAdminContent} from "../pages/page-admin-content";

import {
  AdminIsNotClerkGuard,
  AdminIsNotResideAdminGuard,
  AdminIsOrganizationAdminGuard,
  AdminIsOrganizationItAdminRouteGuard,
  AdminIsOrganizationAdminOnlyGuard,
  AdminIsResideAdminGuard,
  AdminCanManageUsersGuard,
} from "../models/admin-session/AdminHasRole";
import {PageAdminPreflight} from "../pages/page-admin-preflight";
import {FeatureFlagsRouter} from "../pages/page-admin-feature-flags";
import {AdminAppRefresh} from "./AdminAppRefresh";
import {
  AdminApiClientConfiguration,
  RestApiClientConfiguration,
} from "./useApiClientConfiguration";
import {DashboardAdmissionsContextProvider} from "../atoms/dashboard-table/DashboardAdmissionsContext";
import {StonlyConfig} from "../atoms/stonly/StonlyConfig";
import {AutoLogoutProvider} from "../contexts/AutoLogoutContext";
import {AutoLogoutWarningBar} from "../atoms/auto-logout";
import {dispatch} from "../store";
import {useClearAndCancelCache} from "../hooks/useClearAndCancelCache";
import {ErrorBoundary} from "../atoms/error-boundary";
import {SSOLoginRedirectPage} from "../pages/page-admin-sso-login-form/SSOLoginRedirectPage";

/**
 * Guards must be set statically, so the component is not re-mounted, but updated.
 */
const GuardedPageAdminDashboard = AdminIsLoggedIn(PageAdminDashboard);
const GuardedPageAdminFacilitySettings = AdminIsLoggedIn(
  AdminIsNotClerkGuard(PageAdminFacilitySettings),
);
const GuardedPageAdminActivityLog = AdminIsLoggedIn(
  AdminIsNotClerkGuard(PageAdminActivityLog),
);
const GuardedPageAdminUserEdit = AdminIsLoggedIn(PageAdminUserEdit);
const GuardedPageAdminUserCreate = AdminIsLoggedIn(
  AdminCanManageUsersGuard(PageAdminUserCreate),
);
const GuardedPageAdminProfileEdit = AdminIsLoggedIn(PageAdminProfileEdit);
const GuardedPageAdminChangePassword = AdminIsLoggedIn(PageAdminChangePassword);
const GuardedPageAdminUsers = AdminIsLoggedIn(
  AdminIsNotClerkGuard(PageAdminUsers),
);
const GuardedPageAdminAdmissionExperience = AdminIsLoggedIn(
  PageAdminAdmissionExperience,
);
const GuardedPageAdminSearch = AdminIsLoggedIn(PageAdminSearch);
const GuardedPageAdminPreflight = AdminIsLoggedIn(
  AdminIsNotResideAdminGuard(PageAdminPreflight),
);
const GuardedPageAdminOrganizations = AdminIsLoggedIn(
  AdminIsOrganizationAdminGuard(PageAdminOrganizations),
);
const GuardedPageAdminOrganizationsBranding = AdminIsLoggedIn(
  AdminIsOrganizationAdminGuard(PageAdminOrganizationsBranding),
);
const GuardedPageAdminOrganizationsCreate = AdminIsLoggedIn(
  AdminIsResideAdminGuard(PageAdminOrganizationsCreate),
);
const GuardedPageAdminOrganizationsEdit = AdminIsLoggedIn(
  AdminIsOrganizationAdminGuard(PageAdminOrganizationsEdit),
);
const GuardedPageAdminFacilityCreate = AdminIsLoggedIn(
  AdminIsResideAdminGuard(PageAdminFacilityCreate),
);
const GuardedFeatureFlagsRouter = AdminIsLoggedIn(
  AdminIsResideAdminGuard(FeatureFlagsRouter),
);
const GuardedReportingRouter = AdminIsLoggedIn(ReportingRouter);
const GuardedHSRouter = AdminIsLoggedIn(
  AdminIsResideAdminGuard(HubAndSpokeRouter),
);
const GuardedPageAdminContent = AdminIsLoggedIn(
  AdminIsResideAdminGuard(PageAdminContent),
);
const GuardedPageAdminResidents = AdminIsLoggedIn(PageAdminResidents);
const GuardedPageAdminResidentsProfile = AdminIsLoggedIn(ResidentProfile);
const GuardedPageAdminResidentsCreate = AdminIsLoggedIn(
  PageAdminResidentsCreate,
);
const GuardedPageAdminResidentsEdit = AdminIsLoggedIn(PageAdminResidentsEdit);
const GuardedPageAdminOrganizationImportExportUsers = AdminIsLoggedIn(
  AdminCanManageUsersGuard(PageAdminOrganizationImportExportUsers),
);
const GuardedPageAdminSsoConfiguration = AdminIsLoggedIn(
  AdminIsOrganizationAdminOnlyGuard(PageAdminSsoConfiguration),
);
const GuardedPageAdminSsoConfigurationCreate = AdminIsLoggedIn(
  AdminIsOrganizationAdminOnlyGuard(PageAdminSsoConfigurationCreate),
);
const GuardedPageAdminSsoConfigurationEdit = AdminIsLoggedIn(
  AdminIsOrganizationAdminOnlyGuard(PageAdminSsoConfigurationEdit),
);

/**
 * Content component for authenticated admin routes.
 * Note that render is being used, so when matched route is not re-mounted (as component) but updated.
 * This is required for pages which have local state which needs to be persisted on different routes, e.g. user edit form.
 *
 * @returns {JSX.Element} The admin routes content with authentication and auto-logout.
 */
const AdminRoutesContentBase = () => {
  const {clearAndCancelCache} = useClearAndCancelCache();

  // Listen for cross-tab logout via localStorage changes
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      // When admin session token is cleared in another tab
      if (event.key === "persist:root" && event.newValue) {
        try {
          const rootState = JSON.parse(event.newValue);
          const adminSessionStr = rootState.adminSession;
          if (adminSessionStr) {
            const adminSession = JSON.parse(adminSessionStr);
            if (!adminSession.token || adminSession.token === "null") {
              // Admin logged out in another tab, navigate to login
              window.location.href = "/admin";
            }
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const handleLogout = useCallback(async () => {
    clearAndCancelCache();
    await dispatch.adminSession.logout();
  }, [clearAndCancelCache]);

  return (
    <ErrorBoundary>
      <AutoLogoutProvider userType="admin" onLogout={handleLogout}>
        <AutoLogoutWarningBar />
        <AdminAppRefresh>
          <AdminApiClientConfiguration />
          <RestApiClientConfiguration />

          <StonlyConfig />
          <Switch>
            <Route
              exact
              path="/admin/dashboard"
              component={GuardedPageAdminDashboard}
            />
            <Route
              exact
              path="/admin/facility/:facilityId/facility-settings"
              component={GuardedPageAdminFacilitySettings}
            />
            <Route
              exact
              path="/admin/facility/:facilityId/activity-log"
              component={GuardedPageAdminActivityLog}
            />
            <Route
              exact
              path="/admin/residents"
              component={GuardedPageAdminResidents}
            />
            <Route
              exact
              path="/admin/residents/create"
              component={GuardedPageAdminResidentsCreate}
            />
            <Route
              exact
              path="/admin/residents/edit/:resident_uuid"
              component={GuardedPageAdminResidentsEdit}
            />
            <Route
              exact
              path="/admin/residents/:resident_uuid"
              component={GuardedPageAdminResidentsProfile}
            />
            <Route
              exact
              path="/admin/admissions/:admissionId"
              component={GuardedPageAdminAdmissionExperience}
            />
            <Route
              exact
              path="/admin/admissions/:admissionId/section/:sectionId"
              component={GuardedPageAdminAdmissionExperience}
            />
            <Route
              exact
              path="/admin/user/:userId/profile"
              component={GuardedPageAdminProfileEdit}
            />
            <Route
              exact
              path="/admin/user/:userId/change-password"
              component={GuardedPageAdminChangePassword}
            />
            <Route
              exact
              path="/admin/users"
              component={GuardedPageAdminUsers}
            />
            <Route
              path="/admin/users/create"
              component={GuardedPageAdminUserCreate}
            />
            <Route
              path="/admin/users/:userId/edit"
              component={GuardedPageAdminUserEdit}
            />

            <Route
              path="/admin/facility/:facilityId/admissions/:admissionId?"
              component={GuardedPageAdminPreflight}
            />
            <Route
              exact
              path="/admin/organizations"
              component={GuardedPageAdminOrganizations}
            />
            <Route
              exact
              path="/admin/search"
              component={GuardedPageAdminSearch}
            />
            <Route
              exact
              path="/admin/organizations/create"
              component={GuardedPageAdminOrganizationsCreate}
            />
            <Route
              exact
              path="/admin/organizations/:organizationId/edit"
              component={GuardedPageAdminOrganizationsEdit}
            />
            <Route
              exact
              path="/admin/organizations/:organizationId/branding"
              component={GuardedPageAdminOrganizationsBranding}
            />
            <Route
              exact
              path="/admin/organizations/:organizationId/branding/:themeId"
              component={GuardedPageAdminOrganizationsBranding}
            />
            <Route
              exact
              path="/admin/sso/login"
              component={SSOLoginRedirectPage}
            />
            <Route
              exact
              path="/admin/organizations/:organizationId/create-facility"
              component={GuardedPageAdminFacilityCreate}
            />
            <Route
              exact
              path="/admin/organizations/:organizationId/manage-users"
              component={GuardedPageAdminOrganizationImportExportUsers}
            />
            <Route
              exact
              path="/admin/organizations/:organizationId/sso-configuration/create"
              component={GuardedPageAdminSsoConfigurationCreate}
            />
            <Route
              exact
              path="/admin/organizations/:organizationId/sso-configuration/edit"
              component={GuardedPageAdminSsoConfigurationEdit}
            />
            <Route
              exact
              path="/admin/organizations/:organizationId/sso-configuration"
              component={GuardedPageAdminSsoConfiguration}
            />
            <Route
              path="/admin/feature-flags"
              component={GuardedFeatureFlagsRouter}
            />
            <Route path="/admin/hub-and-spoke" component={GuardedHSRouter} />
            <Route path="/admin/reporting" component={GuardedReportingRouter} />
            <Route path="/admin/analytics" component={GuardedReportingRouter} />
            <Route path="/admin/content" component={GuardedPageAdminContent} />
            <Route component={PageAdmin404} />
          </Switch>
        </AdminAppRefresh>
      </AutoLogoutProvider>
    </ErrorBoundary>
  );
};

/**
 * Guarded version of AdminRoutesContent that restricts ORGANIZATION_IT_ADMIN to only access the SSO configuration route.
 */
const AdminRoutesContent = AdminIsOrganizationItAdminRouteGuard(
  AdminRoutesContentBase,
);

/**
 * Main admin routes component that handles both login and authenticated routes.
 * Provides context for dashboard admissions and routes between login and admin content.
 *
 * @returns {JSX.Element} The admin routes component.
 */
export const AdminRoutes = () => (
  <DashboardAdmissionsContextProvider>
    <Switch>
      <Route
        exact
        path="/admin"
        component={AdminIsLoggedOut(() => (
          <>
            <AdminApiClientConfiguration />
            <PageAdminLogin />
          </>
        ))}
      />
      <Route
        exact
        path="/admin/sso-login-form"
        component={AdminIsLoggedOut(() => (
          <>
            <AdminApiClientConfiguration />
            <PageAdminSSOLoginForm />
          </>
        ))}
      />
      <Route path="/admin/" component={AdminRoutesContent} />
    </Switch>
  </DashboardAdmissionsContextProvider>
);
